import { useRunStore } from '../store/runStore';
import { PERK_POOL, type PerkId } from '../game/systems/perks';
import { sfxPerk } from '../game/audio';
import { useT, type TKey } from '../i18n';
import { haptic } from '../telegram';
import { PerkInfoTooltip } from './PerkInfoTooltip';
import { api } from '../api';

const ANIM_BY_INDEX = ['slide-in-left', 'slide-in-up', 'slide-in-right'] as const;

export function PerkChoice() {
  const pending = useRunStore((s) => s.pendingPerkOffer);
  const takePerk = useRunStore((s) => s.takePerk);
  const rerollsRemaining = useRunStore((s) => s.rerollsRemaining);
  const phase = useRunStore((s) => s.phase);
  const { t } = useT();

  const lk = phase === 'level_up' ? '.levelup' : '';

  if (!pending) return null;

  const onPick = (id: PerkId) => {
    sfxPerk();
    haptic.impact('medium');

    // Phase 2u-B: telemetry + skipped log. Fire-and-forget; never awaited.
    // runId uses clientRunId (a real UUID generated at startRun) so that events
    // pass the API's z.string().uuid() validation. The numeric seed is NOT a
    // UUID and caused every perk telemetry event to get a 400 Bad Request.
    const state = useRunStore.getState();
    const offer = state.pendingPerkOffer;
    if (offer) {
      const offerIndex = state.offeredSkippedLog.length;
      const runId = state.clientRunId;
      const skippedIds = offer.filter((p) => p !== id);
      void api.reportTelemetryEvent('perk.picked', { perkId: id, runId, offerIndex });
      for (const candidate of skippedIds) {
        void api.reportTelemetryEvent('perk.skipped', { perkId: candidate, runId, offerIndex });
      }
      state.appendOfferedSkipped(skippedIds);
    }

    takePerk(id);
  };

  const onReroll = () => {
    haptic.tick();
    useRunStore.getState().rerollPerkOffer();
  };

  return (
    <div
      className="scanlines overlay-enter"
      role="dialog"
      aria-modal="true"
      aria-labelledby="perk-choice-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg-overlay)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-4)',
        padding: 'var(--space-4)',
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + var(--space-4))',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + var(--space-4))',
        zIndex: 10,
        overflow: 'auto',
        touchAction: 'pan-y',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div className="scan-band" />

      <div className="label" style={{ color: 'var(--fg-muted)' }}>
        {t((`perk.heading.label${lk}`) as TKey)}
      </div>

      <h2
        id="perk-choice-title"
        className="subheadline pulse"
        style={{ color: 'var(--fg-secondary)', textAlign: 'center' }}
      >
        {t((`perk.heading.title${lk}`) as TKey)}
      </h2>

      <div
        className="label"
        style={{
          color: 'var(--fg-text-dim)',
          textAlign: 'center',
          maxWidth: '22rem',
          lineHeight: 1.5,
        }}
      >
        {t((`perk.heading.lore${lk}`) as TKey)}
      </div>

      <div style={{
        display: 'grid',
        gap: 'var(--space-3)',
        width: '100%',
        maxWidth: '24rem',
      }}>
        {pending.map((id, idx) => {
          const name = t(`perk.${id}.name` as TKey);
          const desc = t(`perk.${id}.desc` as TKey);
          const letter = name.charAt(0);
          const animClass = ANIM_BY_INDEX[idx] ?? 'slide-in-up';
          const perk = PERK_POOL.find((p) => p.id === id);
          const rarity = perk?.rarity ?? 'common';
          return (
            <PerkInfoTooltip key={id} perkId={id}>
              <button
                className={`card-choice rarity-${rarity} ${animClass}`}
                onClick={() => onPick(id)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  animationDelay: `${idx * 60}ms`,
                  animationFillMode: 'both',
                }}
              >
                <span className="rarity-badge" aria-hidden="true" />
                <span
                  aria-hidden="true"
                  style={{
                    width: 44,
                    height: 44,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid var(--fg-primary)',
                    borderRadius: 2,
                    fontSize: '1.4rem',
                    fontWeight: 'bold',
                    color: 'var(--fg-primary)',
                    background: 'rgba(0, 255, 255, 0.08)',
                    boxShadow: '0 0 10px rgba(0, 255, 255, 0.35)',
                    flex: '0 0 auto',
                  }}
                >
                  {letter}
                </span>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
                    <strong style={{
                      color: 'var(--fg-primary)',
                      fontSize: '0.95rem',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}>
                      {name}
                    </strong>
                    <span className="label" style={{ color: 'var(--fg-secondary)' }}>
                      {t('perk.tier')}
                    </span>
                  </div>
                  <span style={{
                    fontSize: '0.78rem',
                    color: 'var(--fg-text-dim)',
                    lineHeight: 1.3,
                  }}>
                    {desc}
                  </span>
                </div>
              </button>
            </PerkInfoTooltip>
          );
        })}
      </div>

      {rerollsRemaining > 0 && (
        <button
          className="card-choice"
          onClick={onReroll}
          style={{
            width: '100%',
            maxWidth: '24rem',
            padding: 'var(--space-3)',
            textAlign: 'center',
            color: 'var(--fg-secondary)',
            fontSize: '0.85rem',
            letterSpacing: '0.04em',
          }}
        >
          {t('perk.reroll').replace('{n}', String(rerollsRemaining))}
        </button>
      )}

      <div className="label" style={{
        opacity: 0.5,
        marginTop: 'var(--space-2)',
        color: 'var(--fg-muted)',
        textAlign: 'center',
      }}>
        {t('perk.footer')}
      </div>
    </div>
  );
}
